import clsx from 'clsx'
import SidebarFloatingAd from 'components/ad/SidebarFloatingAd'
import { getds } from 'ds'
import NC from 'nc/NC'
import React, { useCallback } from 'react'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'
import { NotificationsContext } from 'shared-components/contexts/NotificationsContext'
import { useAffilate } from 'shared-components/hooks/use-affilate'
import { useTaglessAd } from 'shared-components/hooks/use-tagless-ad'
import PostNavigation from 'shared-components/layout/PostNavigation'
import ScrollEventsListener from 'shared-components/service/ScrollEventsListener'
import styleUtils from 'shared-components/styles/util.module.css'
import AdaptiveThreeColumnLayout from 'shared-components/ui/AdaptiveThreeColumnLayout'
import BadgedProductShort from 'shared-components/ui/BadgedProductShort'
import BadgedShort from 'shared-components/ui/BadgedShort'
import BreadCrumbs from 'shared-components/ui/BreadCrumbs'
import Disclaimer from 'shared-components/ui/Disclaimer'
import IconedListItem from 'shared-components/ui/IconedListItem'
import PostShortLine from 'shared-components/ui/PostShortLine'
import PostTags from 'shared-components/ui/PostTags'
import PostTitle from 'shared-components/ui/PostTitle'
import ProductFeatures from 'shared-components/ui/ProductFeatures'
import ReviewHeroLow from 'shared-components/ui/ReviewHeroLow'
import ReviewScores from 'shared-components/ui/ReviewScores'
import ScoreRing from 'shared-components/ui/ScoreRing'
import SidedCategoryMenu from 'shared-components/ui/SidedCategoryMenu'
import SourcesLine from 'shared-components/ui/SourcesLine'
import SplitterLine from 'shared-components/ui/SplitterLine'
import Check from 'shared-svg/Check.svg'
import Close from 'shared-svg/CloseBold.svg'
import { DSReviewProductPageSG } from 'types.sg.ds'
import stylesCommon from './PostTemplate.ReviewTemplate.module.css'
import styles from './ReviewProductTemplate.module.css'
import { useArticleViewed } from 'shared-components/hooks/use-article-viewed'
import UnderTitleAds from 'components/ad/UnderTitleAds'
import Comments from 'shared-components/ui/Comments'
import PostFollowLineUI from 'shared-components/ui/PostFollowLineUI'

interface ReviewProductTemplateProps {
  page: DSReviewProductPageSG
}

const ReviewProductTemplate: React.VFC<ReviewProductTemplateProps> = ({ page }) => {
  useAffilate(page.productBars)
  const { enqueue } = NotificationsContext.useContainer()
  const {
    common: { strings },
  } = CommonDataContext.useContainer()
  useTaglessAd()

  return (
    <>
      <ScrollEventsListener />
      <Disclaimer />
      <div
        className={styleUtils.wrapper}
        ref={useArticleViewed({
          articleId: page.meta?.postId?.toString() ?? null,
          title: page.title,
          url: page.pagePath,
          categoryLevel: 1,
          categoryTitle: page.meta.categories[0]?.label ?? '',
          author: page.authors.authoredBy.map(author => author.name).join(', '),
          wordCount: parseInt(page.meta.dataLayer?.word_count?.toString() ?? '0', 10),
          date: page.meta.dataLayer?.original_pub_date?.toString() ?? '',
        })}
      >
        <BreadCrumbs list={page.breadcrumbs} className={stylesCommon.breadCrumbs} />
        <ReviewHeroLow post={page}>
          <PostTitle title={page.title} subtitle={page.subtitle} />
          <PostShortLine post={page} className={styles.shortLine} />
          <UnderTitleAds ads={page.underTitleAds} />
          <SplitterLine className={clsx(styleUtils.mobileFull, styles.heroSplitter)} />
        </ReviewHeroLow>
        <div className={clsx(styles.infoGrid)}>
          <BadgedProductShort className={styles.badgedProductShort} review={page} />
          <BadgedShort
            className={styles.badgedShort}
            image={page.badges.length ? page.badges[0] : null}
            score={null}
            bottomLine={page.bottomLineContent.text}
            title={page.device.name}
          />
          <ProductFeatures className={styles.features} vars={page.features} />
          <div className={clsx(styles.evaluates, styles.evaluatesPositive)}>
            <div className={styles.h3Title}>{page.table.positive.title}</div>
            {page.table.positive.list.map((item, index) => (
              <IconedListItem icon={Check} key={index} iconClassName={styles.iconPositive}>
                {item}
              </IconedListItem>
            ))}
          </div>
          <div className={clsx(styles.evaluates, styles.evaluatesNegative)}>
            <div className={styles.h3Title}>{page.table.negative.title}</div>
            {page.table.negative.list.map((item, index) => (
              <IconedListItem icon={Close} key={index} iconClassName={styles.iconNegative}>
                {item}
              </IconedListItem>
            ))}
          </div>
          <div className={styles.ringScores}>
            {page.device.ourScore ? (
              <div className={styles.ringScore}>
                <ScoreRing
                  score={page.device.ourScore}
                  variant="base"
                  className={styles.scoreRing}
                />
                {'SoundGuys Rating'}
              </div>
            ) : null}
            {page.device.userScore ? (
              <div className={styles.ringScore}>
                <ScoreRing
                  score={page.device.userScore}
                  variant="violet"
                  className={styles.scoreRing}
                />
                {'User Rating'}
              </div>
            ) : null}
          </div>
          <ReviewScores
            key={page.meta.postId}
            className={styles.scores}
            scores={page.scores}
            mdaqs={page.mdaqs}
            onSumbitNewScore={useCallback(
              async newVal => {
                const dataSource = await getds()
                await dataSource.sendScore(newVal)
                enqueue({ msg: strings.msgSuccess, duration: 3000, variant: 'success' })
                return true
              },
              [enqueue, strings]
            )}
          />
          <SplitterLine className={styles.splitter} />
        </div>

        <AdaptiveThreeColumnLayout
          left={
            page.sideMenu.length ? (
              <SidedCategoryMenu
                className={stylesCommon.sidebarPostNav}
                title="Go deeper"
                menu={page.sideMenu}
              />
            ) : null
          }
          content={
            <>
              {page.blocks.map((item, index) => (
                <NC key={index + 1} data={item} tpl="text" index={index} />
              ))}

              {page.sources.length ? (
                <SourcesLine className={styleUtils.nc} sources={page.sources} />
              ) : null}

              <PostTags meta={page.meta} className={stylesCommon.postTags} />

              <PostFollowLineUI
                className={stylesCommon.followLine}
                authors={page.authors.authoredBy}
                topics={page.meta.topics}
                categories={page.meta.categories}
              />

              {page.pagePath && page.meta.engagementContainerId ? (
                <Comments className={stylesCommon.comments} />
              ) : null}
            </>
          }
          sidebar={
            <PostNavigation>
              {[...page.sidebarContent, ...page.sidebarAds].map((item, index) => (
                <NC key={index} data={item} tpl="sidebar" index={index} />
              ))}
              <SidebarFloatingAd />
            </PostNavigation>
          }
        />
        {/* <RecommendSG
          containerClassName={stylesCommon.related}
          itemClassName={styleRelatedCard.relatedCard}
          title="More from SoundGuys"
        /> */}
      </div>
    </>
  )
}

export default ReviewProductTemplate
